import { useConfig } from 'config';
import { ShopId } from 'constants/shop';
import { useContentful } from 'modules/content-cms/components/context';
import Head from 'next/head';
import React from 'react';
import { jsonLdScriptProps } from 'react-schemaorg';
import { BreadcrumbList, Product } from 'schema-dts';
import { isSubscriptionEnabled, minusPercent } from 'utils/product';
import { generateVariantUrl } from 'utils/shopify';
import canonical from '../../components/seo/canonical';
import description from '../../components/seo/description';
import image from '../../components/seo/image';
import robots from '../../components/seo/robots';
import pageTitle from '../../components/seo/title';
import seotype from '../../components/seo/type';
import { capitalize } from '../../utils/strings';
import { ProductDetailsPage } from './types';

export interface ProductDetailsHeadProps {
  product: ProductDetailsPage['productDetails']['product'];
}

const DEFAULT_SUBSCRIPTION_TYPES = [
  'Nassfutter',
  'Trockenfutter',
  'Frische-Menüs',
];

const ProductDetailsHead: React.FC<ProductDetailsHeadProps> = ({ product }) => {
  const {
    commonCategories,
    frontendUrl,
    shopId,
    shopifyUrl,
    subscriptionDiscount,
  } = useConfig();
  const subscriptionEnabled = isSubscriptionEnabled(
    product.tags,
    Number(product.id)
  );
  const isIncluded = DEFAULT_SUBSCRIPTION_TYPES.includes(product.productType);

  const { data: contentful } = useContentful();
  const url = frontendUrl;

  return (
    <Head>
      {pageTitle(contentful.fields.headline)}
      {seotype('product')}
      {image(
        shopId === ShopId.DE
          ? `https://d3kux32f7234xo.cloudfront.net/${product.handle}.jpg`
          : product.images[0].originalSrc
      )}
      {description(contentful.fields.metaDescription)}
      {robots(contentful.fields.metaRobots)}
      {canonical(contentful.fields.canonical || contentful.fields.handle)}

      <script
        {...jsonLdScriptProps<BreadcrumbList>({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: `${
                product.title.includes(capitalize(commonCategories.dogs))
                  ? capitalize(commonCategories.dogs)
                  : capitalize(commonCategories.cats)
              }`,
              item: `${frontendUrl}/pages/${
                product.title.includes(capitalize(commonCategories.dogs))
                  ? commonCategories.dogs
                  : commonCategories.cats
              }`,
            },
            {
              '@type': 'ListItem',
              position: 2,
              name:
                contentful.fields.mainCollection?.fields
                  .breadcrumbsCollectionTitle || contentful.fields.headline,
              item: `${frontendUrl}/${contentful.fields.mainCollection?.fields.handle}`,
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: product.metadata.accentuate.mainTitle || product.title,
              item: `${frontendUrl}/${contentful.fields.handle}`,
            },
          ],
        })}
      />

      <script
        {...jsonLdScriptProps<Product>({
          '@context': 'https://schema.org',
          '@type': 'Product',
          // This is to make search engine to recognize Judge.me JSON-LD is associated with this scheme.
          '@id': `${url}#product`,
          name: contentful.fields.headline,
          url,
          brand: {
            '@type': 'Brand',
            name: 'Pets Deli',
          },
          description:
            (contentful && contentful.fields.metaDescription) ||
            product.meta_description,
          image: product.images[0].originalSrc,
          // When only 1 variant is available, assign sku & gtin in product level
          ...(product.variants.length === 1 && {
            sku: product.variants[0].sku,
            gtin: product.variants[0].barcode,
          }),
          offers: product.variants.map((variant) => {
            // logic to check the existence of the strike trough price and possibly show that price,
            // plus the lowest price in the case of a product where default subscription is offered
            const subscriptionPrice =
              isIncluded && subscriptionEnabled && shopId === ShopId.DE
                ? minusPercent(
                    Number(variant.priceV2.amount),
                    subscriptionDiscount
                  )
                : undefined;

            const price = subscriptionPrice || Number(variant.priceV2.amount);

            const salePrice = subscriptionPrice
              ? Number(variant.priceV2.amount)
              : variant.compareAtPrice || undefined;

            return {
              '@type': 'Offer',
              availability: variant.availableForSale ? 'InStock' : 'OutOfStock',
              url: generateVariantUrl(
                `${url}/products/${product.handle}`,
                variant
              )
                .replace(shopifyUrl, frontendUrl)
                .replace('https://staging.petsdeli.ch', frontendUrl),
              priceCurrency: variant.priceV2.currencyCode,
              price: salePrice ?? price,
              ...(salePrice && { sale_price: price }),
              itemCondition: 'NewCondition',
              category: product.productType,
              ...(product.variants.length > 1 && {
                sku: variant.sku,
                gtin: variant.barcode,
              }),
            };
          }),
        })}
      />
    </Head>
  );
};

export default ProductDetailsHead;
